/**
 * 계약 호출 — shared 의 라우트 계약 하나로 경로·메서드·응답 검증까지 (hc 없음: nmstory D133 TS2589).
 * 응답을 계약 스키마로 파싱한다 — 서버가 계약을 어기면 화면이 조용히 깨지지 않고 오류로 드러난다.
 */
import {
  buildPath,
  type RouteBody,
  type RouteDef,
  type RouteParams,
  type RouteQuery,
  type RouteResponse,
} from '@geo-admin/shared/contract/route.ts';
import { IMPERSONATION_HEADER, REALM_HEADER } from '@geo-admin/shared/contract/session.ts';
import { impersonation } from './impersonation.ts';
import { realmOfPath } from './paths.ts';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export interface CallInput<R extends RouteDef> {
  params?: RouteParams<R>;
  query?: RouteQuery<R>;
  body?: RouteBody<R>;
}

export async function call<R extends RouteDef>(route: R, input: CallInput<R> = {}): Promise<RouteResponse<R>> {
  const path = buildPath(route.path, input.params as Record<string, string> | undefined);
  const q = input.query
    ? Object.entries(input.query as Record<string, unknown>).filter(([, v]) => v !== undefined && v !== '')
    : [];
  const url = q.length ? `${path}?${new URLSearchParams(q.map(([k, v]) => [k, String(v)]))}` : path;
  const headers = sessionHeaders();
  // 본문이 없어도 JSON 으로 — 서버의 CSRF 검사는 폼 전송형 요청을 막는다
  if (route.method !== 'get') headers['content-type'] = 'application/json';
  const res = await fetch(url, {
    method: route.method.toUpperCase(),
    headers,
    body: input.body === undefined ? undefined : JSON.stringify(input.body),
    credentials: 'same-origin',
  });
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    /* 비 JSON */
  }
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? 'http', err?.message ?? `HTTP ${res.status}`);
  }
  const parsed = route.response.safeParse(json);
  if (!parsed.success) {
    console.error('계약 위반 응답', route.path, parsed.error.issues);
    throw new ApiError(0, 'contract-violation', `서버 응답이 계약과 다릅니다: ${route.path}`);
  }
  return parsed.data as RouteResponse<R>;
}

/**
 * 어느 세션으로 부를지 — 운영자·고객 쿠키가 함께 실리므로 지금 화면의 영역을 알린다 (260917-realm-sessions-impersonation).
 * 대리 탭의 고객 영역이면 대리 토큰을 싣는다. 서버는 이 헤더가 있으면 쿠키를 보지 않는다.
 */
export function sessionHeaders(): Record<string, string> {
  const realm = realmOfPath(window.location.pathname);
  const headers: Record<string, string> = { [REALM_HEADER]: realm };
  const imp = impersonation();
  // 대리 세션은 사용자 영역에만 있다
  if (imp && realm !== 'admin') headers[IMPERSONATION_HEADER] = imp.token;
  return headers;
}

/**
 * 헤더를 실어야 여는 파일(대리 탭의 샘플 원본) — `<a href>` 는 헤더를 못 싣는다. 새 창을 먼저 열어(팝업 차단 회피)
 * blob 으로 채운다. 비운영자가 볼 수 있는 원본은 png·txt 뿐이라 blob 으로 열어도 스크립트가 돌지 않는다.
 */
export async function openWithSession(url: string): Promise<boolean> {
  const tab = window.open('', '_blank');
  if (!tab) return false;
  const res = await fetch(url, { headers: sessionHeaders(), credentials: 'same-origin' }).catch(() => null);
  if (!res?.ok) {
    tab.close();
    return false;
  }
  const blobUrl = URL.createObjectURL(await res.blob());
  tab.location.href = blobUrl;
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  return true;
}
